import  { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Grid,
  Image,
  Loader,
  FileInput,
  NumberInput,
  SimpleGrid,
  Card,
  ActionIcon,
  Text,
  Modal,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import axios from 'axios';
import { useAppSelector } from '../../../../store/hooks';
import { IconTrash, IconEdit, IconUpload } from '@tabler/icons-react';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  imageUrl: string;
  order: number;
  active: boolean;
}

export const HeroGalleryPage = () => {
  const { token } = useAppSelector((state) => state.auth);
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HeroSlide | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      title: '',
      subtitle: '',
      ctaText: '',
      ctaLink: '',
      order: 0,
      active: true,
    },
  });

  const fetchSlides = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery/HOME_HERO`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const gallery = res.data;
      const items = gallery.items
        .filter((item: any) => item.type === 'SLIDE')
        .map((item: any) => {
          let meta: any = {};
          try {
            meta = JSON.parse(item.metadata || '{}');
          } catch (e) {}
          return {
            id: item.id,
            title: meta.title || item.title || '',
            subtitle: meta.subtitle || '',
            ctaText: meta.ctaText || '',
            ctaLink: meta.ctaLink || '',
            imageUrl: item.imageUrl,
            order: meta.order || 0,
            active: item.active,
          };
        })
        .sort((a: HeroSlide, b: HeroSlide) => a.order - b.order);
      setSlides(items);
    } catch (error) {
      console.error('Failed to fetch hero slides:', error);
      notifications.show({ title: 'Error', message: 'Failed to load slides', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const resetForm = () => {
    form.reset();
    setImageFile(null);
    setEditingId(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!imageFile && !editingId) {
      notifications.show({ title: 'Error', message: 'Please select an image', color: 'red' });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    const metadata = {
      title: values.title,
      subtitle: values.subtitle,
      ctaText: values.ctaText,
      ctaLink: values.ctaLink,
      order: values.order,
    };
    const data = {
      title: values.title,
      description: '',
      type: 'SLIDE',
      active: values.active,
      orderIndex: 0,
      metadata: JSON.stringify(metadata),
    };
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob);
    if (imageFile) formData.append('file', imageFile);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/gallery/items/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Slide updated', color: 'green' });
      } else {
        await axios.post(`${API_URL}/gallery/HOME_HERO/items`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Slide added', color: 'green' });
      }
      resetForm();
      fetchSlides();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Operation failed', color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/gallery/items/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: 'Success', message: 'Slide deleted', color: 'green' });
      closeDeleteModal();
      fetchSlides();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Delete failed', color: 'red' });
    } finally {
      setSubmitting(false);
      setDeleteTarget(null);
    }
  };

  const editSlide = (slide: HeroSlide) => {
    setEditingId(slide.id);
    form.setValues({
      title: slide.title,
      subtitle: slide.subtitle,
      ctaText: slide.ctaText,
      ctaLink: slide.ctaLink,
      order: slide.order,
      active: true,
    });
    setImageFile(null);
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Title order={2} mb="lg">Manage Hero Slides</Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">{editingId ? 'Edit Slide' : 'Add New Slide'}</Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Title *"
                  placeholder="Slide title"
                  {...form.getInputProps('title')}
                  required
                />
                <Textarea
                  label="Subtitle"
                  placeholder="Slide subtitle"
                  {...form.getInputProps('subtitle')}
                />
                <TextInput
                  label="CTA Button Text"
                  placeholder="e.g., Shop Now"
                  {...form.getInputProps('ctaText')}
                />
                <TextInput
                  label="CTA Button Link"
                  placeholder="/products"
                  {...form.getInputProps('ctaLink')}
                />
                <NumberInput
                  label="Display Order"
                  placeholder="0, 1, 2..."
                  {...form.getInputProps('order')}
                />
                <FileInput
                  label="Slide Image"
                  placeholder="Click to upload"
                  accept="image/png,image/jpeg,image/svg+xml"
                  value={imageFile}
                  onChange={setImageFile}
                  leftSection={<IconUpload size={16} />}
                />
                <Group justify="flex-end" mt="md">
                  {editingId && (
                    <Button variant="light" onClick={cancelEdit}>Cancel</Button>
                  )}
                  <Button type="submit" color="primary" loading={submitting}>
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">Existing Slides ({slides.length})</Title>
            {slides.length === 0 ? (
              <Text c="dimmed">No slides added yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {slides.map((slide) => (
                  <Card key={slide.id} withBorder padding="sm">
                    <Card.Section>
                      <Image
                        src={slide.imageUrl.startsWith('http') ? slide.imageUrl : `${API_URL}${slide.imageUrl}`}
                        height={100}
                        fit="cover"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200' viewBox='0 0 400 200'%3E%3Crect width='400' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E"
                      />
                    </Card.Section>
                    <Stack mt="xs" gap={5}>
                      <Text fw={600}>{slide.title}</Text>
                      <Text size="xs" c="dimmed">{slide.subtitle}</Text>
                      <Text size="xs">CTA: {slide.ctaText} → {slide.ctaLink}</Text>
                      <Text size="xs">Order: {slide.order}</Text>
                      <Group justify="space-between" mt="xs">
                        <ActionIcon color="blue" onClick={() => editSlide(slide)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon color="red" onClick={() => { setDeleteTarget(slide); openDeleteModal(); }}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Confirm Deletion">
        <Text>Are you sure you want to delete the slide <strong>{deleteTarget?.title}</strong>? This action cannot be undone.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={submitting}>Delete</Button>
        </Group>
      </Modal>
    </Container>
  );
};